import React, { useMemo } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import DiagnosisSelectField from '../../../../Components/Base/Diagnosis/DiagnosisSelectField';
import FormMultiplierRemoveButton from '../../../../Components/Form/FormMultiplierRemoveButton';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import FormMultiplier from '../../../../Components/Form/FormMultiplier';
import InputField from '../../../../Components/Form/components/InputField';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import { PredefineDiscoursePhrase } from './PredefinedPhrasesDiscourseSetup';
import apiClient from '../../../../Services/ApiClient';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useAppContext } from '../../../../Services/AppContext';
import { DocumentType } from '../../../../graphql/graphql';
import { prepareDocumentDataChildren } from '../../Services/discrousePredifinedPhrase.service';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';

interface Props {
	defaultValue?: PredefineDiscoursePhrase;
	onSuccess: () => void;
}

export interface PredefinedPhrasesFormValues {
	name?: string;
	text: string;
	diagnosis?: SelectValue;
	diagnoses?: { secondaryDiagnosis: SelectValue }[];
}

const PredefinedPhrasesForm: React.FC<Props> = ({
	defaultValue,
	onSuccess,
}) => {
	const { showNotificationSuccess } = useNotification();
	const { workplaceId, userData } = useAppContext();
	const { t } = useTranslation();
	const { processResult } = useRestApiClient();

	const transformToPredefinedPhrasesFormValues = (
		defaultValue: PredefineDiscoursePhrase,
	): PredefinedPhrasesFormValues => {
		return defaultValue?.items.reduce<PredefinedPhrasesFormValues>(
			(acc, item) => {
				switch (item.type) {
					case 'content':
						if (typeof item.extra.text === 'string') acc.text = item.extra.text;
						break;
					case 'name':
						if (typeof item.extra.text === 'string') acc.name = item.extra.text;
						break;
					case 'diagnosis':
						if (item.extra.subType === 'main_diagnosis') {
							if (typeof item.extra.text !== 'string')
								acc.diagnosis = item.extra.text;
						} else if (item.extra.subType === 'secondary_diagnosis') {
							acc.diagnoses = acc.diagnoses || [];
							if (typeof item.extra.text !== 'string')
								acc.diagnoses.push({ secondaryDiagnosis: item.extra.text });
						}
						break;
				}
				return acc;
			},
			{ text: '' }, //Default value, to dont text let be undefined
		);
	};

	const defaultValues: object = useMemo(() => {
		if (defaultValue) {
			return transformToPredefinedPhrasesFormValues(defaultValue);
		}
		return {};
	}, [defaultValue]);

	const validationSchema = yup.object({
		text: yup.string().required(t('validation.predefinedPhrasesForm.value')),
		name: yup.string().notRequired(),
	});

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const handleSubmit = async (values: any) => {
		const data = {
			workplaceId,
			userId: userData.id,
			createdById: userData.id,
			type: DocumentType.DekurzTemplate,
			children: prepareDocumentDataChildren(
				values as PredefinedPhrasesFormValues,
			),
		};
		if (defaultValue) {
			const result = await apiClient.patch(
				`/document/${defaultValue.id}`,
				data,
			);
			processResult(result, () => {
				showNotificationSuccess({
					content: t('component.predefinedPhrasesForm.editSuccess'),
				});
				onSuccess();
			});
		} else {
			const result = await apiClient.post(`/document`, data);
			processResult(result, () => {
				showNotificationSuccess({
					content: t('component.predefinedPhrasesForm.addSuccess'),
				});
				onSuccess();
			});
		}
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<InputField
				name='name'
				label='component.predefinedPhrasesForm.name'
				isWrapped
			/>
			<TextAreaField
				name='text'
				label='component.predefinedPhrasesForm.value'
				isWrapped
				isRequired
			/>
			<InputWrapper>
				<DiagnosisSelectField
					name='diagnosis'
					label='component.ePrescription.mainDiagnosis'
				/>
			</InputWrapper>
			<FormMultiplier
				name='diagnoses'
				addButtonTitle='base.addAnother'
				createDefault={1}>
				<InputWrapper>
					<DiagnosisSelectField
						name='secondaryDiagnosis'
						label='component.ePrescription.secondaryDiagnosis'
					/>
					<FormMultiplierRemoveButton>
						<RemoveButton onClick={() => void 0} title='base.shared.remove' />
					</FormMultiplierRemoveButton>
				</InputWrapper>
			</FormMultiplier>
			<SubmitButton
				onSubmit={handleSubmit}
				title={defaultValue ? 'base.shared.edit' : 'base.create'}
			/>
		</FormWrapper>
	);
};

export default PredefinedPhrasesForm;
